import React from "react";

// Hooks
import useFetchCategoryMixedContent from "../../../../../Hooks/Content/Watch/MixedContentFeed/useFetchCategoryMixedContent";
import useFetchJustReleased from "../../../../../Hooks/Content/Watch/VideoFeed/useFetchJustReleased";
import LandscapeContentItem from "../../LandscapeContentItem";

// Models
import {AuthUser} from "../../../../../Models/DTOs/AuthUser";

// Helpers
import {createPlaceholderICarouselContentDTO} from "../../../../../Helpers/Content/CarouselContent";

interface VideoRelatedVideosProps {
    videoId: number;
    videoCategoryId: number | null | undefined;
    additionalCategories?: number[]; // Made optional to account for undefined state
    parentContentStillLoading: boolean;
    userData: AuthUser | null;
    doesAuthTokenExist: () => boolean;
}

const VideoRelatedVideos: React.FC<VideoRelatedVideosProps> = (
    {
        videoId,
        videoCategoryId,
        additionalCategories = [],
        parentContentStillLoading,
        userData,
        doesAuthTokenExist
    }
) => {

    // User authentication data
    const
        // Determine the effective category ID based on props
        effectiveCategoryId =
            (videoCategoryId !== null && videoCategoryId !== undefined)
                ? videoCategoryId
                : (!parentContentStillLoading && additionalCategories.length > 0
                        ? additionalCategories[0]
                        : null
                ),

        // Determine if we should fetch category content
        shouldFetchCategory = effectiveCategoryId !== null,

        // Fetch category content if applicable
        {
            content: categoryContent,
            loading: categoryLoading,
            error: categoryError
        } = useFetchCategoryMixedContent(
            shouldFetchCategory ? effectiveCategoryId : null, // Pass null to indicate no fetch
            30, 0, false, "desc", true
        ),

        // Determine if we should fetch just released content
        shouldFetchJustReleased =
            !parentContentStillLoading &&
            videoCategoryId == null &&
            additionalCategories.length === 0,

        // Fetch just released content if applicable
        {
            justReleased,
            justReleasedLoading,
            justReleasedError
        } = useFetchJustReleased(),

        // Determine loading and error states
        isLoading = categoryLoading || justReleasedLoading || parentContentStillLoading,
        hasError = categoryError || justReleasedError,

        dummyContent = createPlaceholderICarouselContentDTO(12),

        filteredCategoryContent = categoryContent?.filter(video => video.Id !== videoId),
        filteredJustReleasedContent = justReleased?.slice(0).filter(video => video.Id !== videoId);

    return (
        <div className="watch-video__related-videos">
            <div>
                <h2>Discover more</h2>

                {/* Render category-related watch */}
                {!isLoading && shouldFetchCategory && filteredCategoryContent && filteredCategoryContent.length > 0 && filteredCategoryContent.map((video) => (
                    <LandscapeContentItem
                        key={video.Id} // Ensure each child has a unique key
                        item={video}
                        userData={userData}
                        authTokenExists={doesAuthTokenExist()}
                        isLoading={isLoading}
                    />
                ))}

                {/* Render just released content */}
                {!isLoading && shouldFetchJustReleased && filteredJustReleasedContent && filteredJustReleasedContent.length > 0 && filteredJustReleasedContent.map((video) => (
                    <LandscapeContentItem
                        key={video.Id} // Ensure each child has a unique key
                        item={video}
                        userData={userData}
                        authTokenExists={doesAuthTokenExist()}
                        isLoading={isLoading}
                    />
                ))}

                {/* Fallback if no content is available */}
                {!isLoading && !shouldFetchCategory && !shouldFetchJustReleased && !isLoading && (
                    <h2>No Related Videos Available</h2>
                )}

                {!isLoading && hasError && (
                    <h2>Error loading related videos. Please try again later.</h2>
                )}

                {isLoading && dummyContent.map((video) => (
                    <LandscapeContentItem
                        key={video.Id} // Ensure each child has a unique key
                        item={video}
                        userData={userData}
                        authTokenExists={doesAuthTokenExist()}
                        isLoading={isLoading}
                    />
                ))}
            </div>
        </div>
    );
};

export default VideoRelatedVideos;
